import getCSSVariableValue from "components/ProjectNonSpecificComponents/Functions/getCSSVariableValue"
import moveArrItem from "components/ProjectNonSpecificComponents/Functions/moveArrItem"
import ensureHashtag from "../../Functions/ensureHashtag"
import hexCodeRegex from "../../Functions/hexCodeRegex"
import _ from "lodash"
import {
	TMultiFieldFormKey,
	TPartialMultiFieldFormData,
	TPartialSingleObjectFormData,
	TSingleObjectFormKey,
} from "../../Interfaces/AdminInterface"
import {
	ICollection,
	IConnection,
	IEmailField,
	IFormsConfig,
	IItemType,
	INewRequestFormField,
	IColor,
	IRequestTableFormField,
	ISavedRequestFormField,
	ISettings,
	IUserIdField,
	TUserField,
	IPersonolize,
	IAdditional,
} from "../../Interfaces/FormsInterface"
import { IJamaItem, IJamaProjectsAndFolders, IMainField } from "../../Interfaces/DataInterface"
import { TAnyValuesObject, TLanguageValues } from "../../Interfaces/GlobalInterface"
import {
	defaultCollectionsFormData,
	defaultConfigData,
	defaultFieldData,
	defaultFormData,
	defaultItemTypeFormData,
	defaultNewRequestFormData,
	defaultPersonalizationFormData,
	defaultRequestTableFormData,
	defaultSavedRequestFormData,
	defaultSettingsData,
	defaultUserFormItemOfTypeLevels,
	defaultUserIdFieldFormData,
	defaultUserSelectedForm,
	getDefaultFormData,
} from "./defaults"
import { TReactSelectOption } from "components/ProjectSpecificComponents/Interfaces/ExternalComponentsInterfaces"
import { defaultAllJamaItems } from "../PTKMStore/defaults"
import AppRequests from "components/ProjectSpecificComponents/API/AppRequests/AppRequests"
import getStoreProperty from "components/ProjectNonSpecificComponents/DevTools/Functions/getStoreProperty"

/**
 * Store only and only for form **data**.
 */
export default function createStore() {
	return {
		/**
		 * language change for induvidual section (for custom input names)
		 */
		newRequestLanguage: defaultLanguage,
		setNewRequestLanguage(lng: TLanguageValues) {
			this.newRequestLanguage = lng
		},
		requestTableLanguage: defaultLanguage,
		setRequestTableLanguage(lng: TLanguageValues) {
			this.requestTableLanguage = lng
		},
		savedRequestLanguage: defaultLanguage,
		setSavedRequestLanguage(lng: TLanguageValues) {
			this.savedRequestLanguage = lng
		},
		settingsLanguage: defaultLanguage,
		setSettingsLanguage(lng: TLanguageValues) {
			this.settingsLanguage = lng
		},
		setAllLanguages(lng: TLanguageValues) {
			this.newRequestLanguage = lng
			this.requestTableLanguage = lng
			this.savedRequestLanguage = lng
			this.settingsLanguage = lng
		},
		userSelectedForm: defaultUserSelectedForm,
		setUserSelectedForm(_form: IFormsConfig | undefined) {
			this.userSelectedForm = _form
		},

		allJamaItems: defaultAllJamaItems,
		async getAllJamaItems(latest: boolean) {
			if (!this.allJamaItems.fetched && latest) {
				const { ok, data } = await AppRequests("ItemType").get<IJamaItem[]>("all")

				if (ok && data) {
					this.allJamaItems = {
						ALL: data,
						fetched: true,
					}
				}
			}

			return this.allJamaItems.ALL
		},
		/**
		 * Data of user settings json
		 */
		settingsData: settingsData,
		/**
		 * Merges the provided settings data, which may be partial,
		 * with the one in store.
		 */
		mergeSettingsDataSafely(data: Partial<ISettings>) {
			data = { ...data } // shallow copy

			// !TODO:     Is this fn / filter logic even needed?
			//            Ideally, the values that are passed as null DO overwrite
			//            other properties.
			//						Make this a "set" function?
			// Filter out null values, in order for those to not override defaults
			let key: keyof ISettings
			for (key in data) {
				if (data[key] == null) delete data[key]
			}

			this.settingsData = {
				...this.settingsData,
				...data,
			}

			return this.settingsData
		},
		mergeNewRequestFormData(fieldIndex: number, data: Partial<INewRequestFormField>) {
			// Transform provided data, if rules apply
			if (data.mainFieldKey) {
				data = {
					...defaultFieldData["newRequestForm"],
					...data,
				}
			}

			this._mergeMultiFieldFormData("newRequestForm", fieldIndex, data)
		},
		mergeRequestTableFormData(fieldIndex: number, data: Partial<IRequestTableFormField>) {
			// Transform provided data, if rules apply
			if (data.mainFieldKey) {
				data = {
					...defaultFieldData["requestTable"],
					...data,
				}
			}

			this._mergeMultiFieldFormData("requestTable", fieldIndex, data)
		},
		mergeSavedRequestFormData(fieldIndex: number, data: Partial<ISavedRequestFormField>) {
			this._mergeMultiFieldFormData("savedRequestForm", fieldIndex, data)
		},
		mergeCollectionsFormData(fieldIndex: number, data: Partial<ICollection>) {
			this._mergeMultiFieldFormData("collections", fieldIndex, data)
		},

		mergeConnectionFormData(data: Partial<IConnection>) {
			this._mergeSettingsData("connection", data)
		},
		mergeItemFieldFormData(
			data: Partial<IUserIdField> | Partial<IItemType>,
			fieldType: TUserField
		) {
			this._mergeSingleObjectFormData(fieldType, data)
		},

		mergePersonalizationFormData(data: Partial<IColor>) {
			// Ensure hashtags for each provided property

			let item: keyof IColor

			for (item in data) {
				let color = data[item] ?? defaultPersonalizationFormData[item]

				// Remove hashtag, if there is one - to normalize
				if (typeof color === "string") {
					color = color.replaceAll("#", "")

					// Prevent badly formatted colors from being let through
					if (color.startsWith("var") || hexCodeRegex.test(color)) {
						if (color.startsWith("var")) {
							color = getCSSVariableValue(color)
						}

						color = ensureHashtag(color) ?? defaultPersonalizationFormData[item]
						data[item] = color
					} else {
						delete data[item]
					}
				}
			}

			this._mergeSettingsData("personalize", data)
		},
		/**
		 * Merges the provided email field data
		 */
		mergeEmailFormData(data: Partial<IEmailField>) {
			this._mergeSettingsData("emailField", data)
		},
		/**
		 * Merges the provided additional field data
		 */
		mergeUserFormWelcomeTextData(data: Partial<IAdditional>) {
			this._mergeSettingsData("additionalInfo", data)
		},
		/**
		 * Merges the provided settings data, which may be partial,
		 * with the one in store.
		 * Used for whole configuration and not individual ones
		 */
		_mergeSettingsData(
			firstLevelProperty: "connection" | "personalize" | "emailField" | "additionalInfo",
			data:
				| Partial<IConnection>
				| Partial<IFormsConfig>
				| Partial<IPersonolize>
				| Partial<IEmailField>
				| Partial<IAdditional>
		) {
			this.settingsData = {
				...this.settingsData,
				[firstLevelProperty]: {
					...this.settingsData[firstLevelProperty],
					...data,
				},
			}
		},
		/**
		 * If `-1` is passed in parameter `data` then the field is deleted.
		 */
		_mergeMultiFieldFormData(
			formName: TMultiFieldFormKey,
			fieldIndex: number,
			data: TPartialMultiFieldFormData | -1
		) {
			if (this._selectedFormPosition == null) throw SLCTD_FORM_POS_NULL_ERR

			// any: It is assumed that method `_getMultiFieldFormFieldsArr` is flawless
			//      and will always return the correct fields array
			const existingFields: any[] = this._getMultiFieldFormFieldsArr(formName)
			const existingField: TAnyValuesObject =
				existingFields[fieldIndex] ?? defaultFieldData[formName]

			const formsConfigs = this.settingsData.formsConfigs
			formsConfigs[this._selectedFormPosition][formName] =
				data == -1
					? [...existingFields.slice(0, fieldIndex), ...existingFields.slice(fieldIndex + 1)]
					: [
							...existingFields.slice(0, fieldIndex),
							{
								...existingField,
								...data,
							},
							...existingFields.slice(fieldIndex + 1),
					  ]

			this.settingsData = {
				...this.settingsData,
				formsConfigs,
			}
		},
		/**
		 * Used for forms such as "Item type", "User ID field".
		 */
		_mergeSingleObjectFormData(formName: TSingleObjectFormKey, data: TPartialSingleObjectFormData) {
			if (this._selectedFormPosition == null) throw SLCTD_FORM_POS_NULL_ERR

			const existingData: any = this.settingsData.formsConfigs[this._selectedFormPosition][formName]

			this.settingsData.formsConfigs[this._selectedFormPosition][formName] = {
				...existingData,
				...data,
			}
			this.settingsData = {
				...this.settingsData,
			}
		},
		/**
		 * Returns index of the new field
		 */
		insertNewFieldIntoForm(formName: TMultiFieldFormKey) {
			if (this._selectedFormPosition == null) throw SLCTD_FORM_POS_NULL_ERR

			// any: It is assumed that `_getMultiFieldFormFieldsArr` is flawless
			const fieldsArr: any[] = this._getMultiFieldFormFieldsArr(formName)
			const nextIndex = fieldsArr.length

			this.settingsData.formsConfigs[this._selectedFormPosition][formName] = [
				...fieldsArr,
				defaultFieldData[formName],
			]
			this.settingsData = {
				...this.settingsData,
			}

			return nextIndex
		},
		moveFieldInForm(formName: TMultiFieldFormKey, fieldIndexA: number, fieldIndexB: number) {
			if (this._selectedFormPosition == null) throw SLCTD_FORM_POS_NULL_ERR

			const fieldsArr = this._getMultiFieldFormFieldsArr(formName)
			const transformedFormData = moveArrItem(fieldIndexA, fieldIndexB, fieldsArr)

			this.settingsData.formsConfigs[this._selectedFormPosition][formName] = transformedFormData
			this.settingsData = {
				...this.settingsData,
			}
		},
		/**
		 * Returns deleted field's data
		 */
		deleteFormField(formName: TMultiFieldFormKey, fieldIndex: number) {
			// Do not let fields which are absolutely required (by BE)
			// to be deleted in `newRequestForm`
			if (formName == "newRequestForm" && this.mainFieldsData != null) {
				const newRequestFormFieldsArr = this._getMultiFieldFormFieldsArr("newRequestForm")
				const collectionsFieldsArr = this._getMultiFieldFormFieldsArr("collections")
				const userDepField = this._getFieldFormFieldsObject("userDepField")
				const userEmailField = this._getFieldFormFieldsObject("userEmailField")
				const userIdField = this._getFieldFormFieldsObject("userIdField")
				const userMobField = this._getFieldFormFieldsObject("userMobField")
				const userNameField = this._getFieldFormFieldsObject("userNameField")

				const { mainFieldKey } = newRequestFormFieldsArr[fieldIndex]

				const { required = false } =
					this.mainFieldsData.find(({ fieldId }) => fieldId == mainFieldKey) ?? {}

				if (required) return

				// Checks if field is added because of multi collection.
				// If true then field cant be removed.
				const hasMultiCollectionFieldBeenAdded = collectionsFieldsArr.some(
					(item) => item.picklistFieldId === mainFieldKey
				)
				// Checks if field is added because of request item.
				// If true then field cant be removed.
				const hasUserFieldBeenAdded = !!mainFieldKey
					? userDepField?.fieldId === mainFieldKey ||
					  userEmailField?.fieldId === mainFieldKey ||
					  userIdField?.fieldId === mainFieldKey ||
					  userMobField?.fieldId === mainFieldKey ||
					  userNameField?.fieldId === mainFieldKey
					: false

				if (hasMultiCollectionFieldBeenAdded || hasUserFieldBeenAdded) return
			}

			const fieldsArr = this._getMultiFieldFormFieldsArr(formName)
			const fieldData = { ...fieldsArr[fieldIndex] }

			// Deletion of the actual field object inside of form's field array
			this._mergeMultiFieldFormData(formName, fieldIndex, -1)

			return fieldData
		},
		/**
		 * Delete field from new request form without restrictions from required fields
		 */
		deleteNewRequestFormField(fieldId: string) {
			const fieldsArr = this._getMultiFieldFormFieldsArr("newRequestForm")
			const fieldIndex = fieldsArr.findIndex(({ mainFieldKey }) => mainFieldKey == fieldId)

			if (fieldIndex < 0) return

			const fieldData = { ...fieldsArr[fieldIndex] }

			// Deletion of the actual field object inside of form's field array
			this._mergeMultiFieldFormData("newRequestForm", fieldIndex, -1)

			return fieldData
		},
		resetMultiFieldForm(formName: TMultiFieldFormKey) {
			if (this._selectedFormPosition !== null) {
				if (formName == "newRequestForm") {
					if (this.mainFieldsData != null) {
						this.resetMandatoryFields()
						const newRequestFields = this._getMultiFieldFormFieldsArr("newRequestForm")
						const collectionFields = this._getMultiFieldFormFieldsArr("collections")
						for (let index = 0; index < newRequestFields.length; index++) {
							const { mainFieldKey } = newRequestFields[index]

							const { required = false } =
								this.mainFieldsData.find(({ fieldId }) => fieldId == mainFieldKey) ?? {}

							if (required) this.addMandatoryNewRequestFields(newRequestFields[index])

							// Checks if field is added because of multi collection.
							// If true then field cant be removed.
							const hasMultiCollectionFieldBeenAdded = collectionFields.some(
								(item) => item.picklistFieldId === mainFieldKey
							)
							if (hasMultiCollectionFieldBeenAdded)
								this.addMandatoryNewRequestFields(newRequestFields[index])
						}
					}

					return (this.settingsData.formsConfigs[this._selectedFormPosition] = {
						...this.settingsData.formsConfigs[this._selectedFormPosition],
						["newRequestForm"]: this.mandatoryNewRequestFields ?? defaultFormData["newRequestForm"],
					})
				}
				this.settingsData.formsConfigs[this._selectedFormPosition] = {
					...this.settingsData.formsConfigs[this._selectedFormPosition],
					[formName]: defaultFormData[formName],
				}
			}
		},
		ensureRequiredFormFieldsExist(mainFieldsData: IMainField[]) {
			for (let { required, fieldId, name, readOnly } of mainFieldsData) {
				if (!required || readOnly) continue
				/**
				 * Gets all fieldIds used in item section
				 */
				const getItemFieldIds = () => {
					if (!this.selectedFormsConfig) return []
					let {
						userIdField: { fieldId: userIdFieldId },
						userEmailField: { fieldId: userEmailFieldId },
						userMobField: { fieldId: userMobFieldId },
						userDepField: { fieldId: userDepFieldId },
						userNameField: { fieldId: userNameFieldId },
					} = this.selectedFormsConfig
					return [userIdFieldId, userEmailFieldId, userMobFieldId, userDepFieldId, userNameFieldId]
				}

				const itemFieldIds = getItemFieldIds()
				//If required field is already used in item section then doesnt add to newRequestForm
				const fieldAlreadyUsedInItem = itemFieldIds.includes(fieldId)
				if (fieldAlreadyUsedInItem) continue
				// Checks if required field already exists in newRequestForm
				const doesFieldAlreadyExist = this._getMultiFieldFormFieldsArr("newRequestForm").some(
					({ mainFieldKey }) => mainFieldKey === fieldId
				)
				// If required field doesnt exists in newRequestForm then add it
				if (!doesFieldAlreadyExist) {
					let currentNewRequestFormFieldIdx =
						this._getMultiFieldFormFieldsArr("newRequestForm").length
					this.mergeNewRequestFormData(currentNewRequestFormFieldIdx, {
						mainFieldKey: fieldId,
						mainFieldLabel: name,
						isRequired: required,
					})
				}
			}
		},
		/**
		 * Add selected picklist field in collections or item to new request fields and sets to required
		 */
		ensurePicklistFieldValueExist(fieldID: string) {
			const newRequestFormFields = this._getMultiFieldFormFieldsArr("newRequestForm")
			const isNewRequestFormToBeInsertedInto = !newRequestFormFields.some(
				(item) => item.mainFieldKey === fieldID
			)

			let currentNewRequestFormFieldIdx = newRequestFormFields.length
			if (this.mainFieldsData) {
				for (let { fieldId, name } of this.mainFieldsData) {
					if (fieldId !== fieldID) continue

					if (isNewRequestFormToBeInsertedInto) {
						this.mergeNewRequestFormData(++currentNewRequestFormFieldIdx, {
							mainFieldKey: fieldId,
							mainFieldLabel: name,
							isRequired: true,
						})
					}
				}
			}
		},
		/**
		 * Gets currently selected form configuration's fields array
		 * of a multi-field form.
		 */
		_getMultiFieldFormFieldsArr<T extends TMultiFieldFormKey>(formName: T): IFormsConfig[T] {
			if (this._selectedFormPosition == null) throw SLCTD_FORM_POS_NULL_ERR
			return this.settingsData.formsConfigs[this._selectedFormPosition][formName]
		},
		/**
		 * Gets currently selected form configuration's fields array
		 * of a multi-field form.
		 */
		_getFieldFormFieldsObject<T extends TSingleObjectFormKey>(formName: T): IFormsConfig[T] {
			if (this._selectedFormPosition == null) throw SLCTD_FORM_POS_NULL_ERR
			return this.settingsData.formsConfigs[this._selectedFormPosition][formName]
		},

		/**
		 * Clears all data from admin panel forms,
		 * in the currently selected form configuration.
		 */
		clearAdminPanelPageForms() {
			if (this._selectedFormPosition == null) throw SLCTD_FORM_POS_NULL_ERR

			// Replace every property that holds form data
			// with default constants
			this.settingsData.formsConfigs[this._selectedFormPosition] = {
				...this.settingsData.formsConfigs[this._selectedFormPosition],
				itemType: defaultItemTypeFormData,
				userIdField: defaultUserIdFieldFormData,
				collections: defaultCollectionsFormData,
				newRequestForm: defaultNewRequestFormData,
				requestTable: defaultRequestTableFormData,
				savedRequestForm: defaultSavedRequestFormData,
			}
			this.settingsData = {
				...this.settingsData,
			}
		},
		/**
		 * Clears all form configurations
		 */
		clearFormsConfigurations() {
			this.settingsData = {
				...this.settingsData,
				formsConfigs: [],
			}
		},
		/**
		 * Add new configuration
		 */
		insertNewFormsConfiguration(data: Partial<IFormsConfig>) {
			const defaultFormData = getDefaultFormData(data)

			let { id, position } = defaultFormData
			// can not trust position from user
			// set id for config
			this.setSelectedForm(id)

			const newFormsConfigs = [
				...this.settingsData.formsConfigs.slice(0, position),
				defaultFormData,
				...this.settingsData.formsConfigs.slice(position),
			]

			this._keepInternalFormsConfigPropertiesInSync(newFormsConfigs) // BE gives position now

			this.settingsData = {
				...this.settingsData,
				formsConfigs: newFormsConfigs,
			}
		},
		/**
		 * Edit each configurations data
		 */
		appendFormsConfiguration(id: number, data?: Partial<IFormsConfig>) {
			if (data?.position == null) return
			this.settingsData.formsConfigs[id] = {
				...this.settingsData.formsConfigs[id],
				...data,
			}
			// remove obj and store it
			const objToMove = this.settingsData.formsConfigs.splice(id, 1)[0]
			// insert stored obj into position
			this.settingsData.formsConfigs.splice(data?.position, 0, objToMove)
			this._keepInternalFormsConfigPropertiesInSync(this.settingsData.formsConfigs)
		},
		/**
		 * Delete selected configuration
		 */
		deleteFormsConfiguration(position: number) {
			// Make it impossible to delete an active configuration
			if (this.isFormsConfigurationActive(position)) return

			const newFormsConfigs = [
				...this.settingsData.formsConfigs.slice(0, position),
				...this.settingsData.formsConfigs.slice(position + 1),
			]

			this._keepInternalFormsConfigPropertiesInSync(newFormsConfigs)

			this.settingsData = {
				...this.settingsData,
				formsConfigs: newFormsConfigs,
			}
		},
		/**
		 * Determines whether the form configuration
		 * at position X is active or not.
		 */
		// TODO position usage dangerous
		isFormsConfigurationActive(position: number) {
			return this.settingsData.formsConfigs[position].isActive
		},
		/**
		 * Determines whether the form configuration
		 * at position X is active or not.
		 */
		isFormsConfigurationActiveId(Id: string) {
			const configIndex = this.settingsData.formsConfigs.findIndex(({ id }) => id == Id)
			if (configIndex < 0) return
			return this.settingsData.formsConfigs[configIndex].isActive
		},
		/**
		 * Keeps "internal" properties of a form's configuration in-sync
		 * with actual facts about them (index = position).
		 * Should be called every insert and delete (array slicing) operation.
		 */
		_keepInternalFormsConfigPropertiesInSync(formsConfigs: IFormsConfig[]) {
			for (let i = 0; i < formsConfigs.length; ++i) {
				let formsConfig = formsConfigs[i]
				formsConfig.position = i
			}
		},

		get selectedFormsConfig(): IFormsConfig | null {
			if (this._selectedFormPosition === null) return null

			// It is remade into a new object
			// in order to trigger component state updates

			const matchedConfigs = this.settingsData.formsConfigs.filter(
				(cnfg) => cnfg.position == this._selectedFormPosition
			)
			const returnCnfg = matchedConfigs.length >= 1 ? matchedConfigs[0] : null

			if (returnCnfg) {
				return { ...returnCnfg }
			} else {
				return null
			}
		},
		get selectedFormsConfigId(): IFormsConfig | undefined {
			if (this.selectedForm == null) return undefined
			const configIndex = this.settingsData.formsConfigs.findIndex(
				({ id }) => id == this.selectedForm
			)
			if (configIndex < 0) return
			// It is remade into a new object
			// in order to trigger component state updates
			return {
				...this.settingsData.formsConfigs[configIndex],
			}
		},

		selectedForm: selectedForm,
		setSelectedForm(id: string) {
			return (this.selectedForm = id)
		},

		/**
		 * Is a private property,
		 * because it is not a piece of data
		 * and is used internally for correct functionality purposes.
		 */
		_selectedFormPosition: selectedFormPosition,
		setSelectedFormsConfig(position: number | null) {
			if (position == null || position > this.settingsData.formsConfigs.length || position < 0) {
				this._selectedFormPosition = null
				return
			}

			this._selectedFormPosition = position
		},

		mainFieldsData: mainFieldsData,
		setMainFieldsData(data: IMainField[] | null) {
			this.mainFieldsData = data
		},

		/**
		 * User side, for form generating and updating certain fields/field values/select options.
		 */
		// !TODO: The property `userFormFields` and its two functions
		//        are all used in only one file:
		//        Decide whether it is really needed as a FormStore functions,
		//        and, if not, delete it, replacing it with a useState.
		//
		//        The same issue is with `projectsAndFoldersData`.
		//        Though it is used in two separate files,
		//        it seems that its API call can be just moved to the other
		//        and give the property same treatement of deletion from FormStore.
		userFormFields: userFormFields,
		setUserFormFields(formData: INewRequestFormField[]) {
			this.userFormFields = formData
		},
		mergeUserFormFields(index: number, data: Partial<INewRequestFormField>) {
			this.userFormFields = [
				...this.userFormFields.slice(0, index),
				{
					...(this.userFormFields[index] ?? {}),
					...data,
				},
				...this.userFormFields.slice(index + 1),
			]
		},
		/**
		 * Saves all projects with folders
		 */
		projectsAndFoldersData: projectsAndFoldersData,
		setProjectsAndFoldersData(data: IJamaProjectsAndFolders[] | null) {
			this.projectsAndFoldersData = data
		},
		/**
		 * Mandatory newRequestForm fields
		 */
		mandatoryNewRequestFields: defaultNewRequestFormData,
		addMandatoryNewRequestFields(data: INewRequestFormField) {
			this.mandatoryNewRequestFields = [...this.mandatoryNewRequestFields, data]
		},
		resetMandatoryFields() {
			this.mandatoryNewRequestFields = []
		},
		/**
		 * Saves selected colors before opening settings.
		 */
		personalizeSettingsBeforeColorChange: defaultPersonalizationFormData,
		/**
		 * Saves personalize settings before color change so on close can return to previously selected colors
		 */
		savePersonalizeSettingsForHistory() {
			this.personalizeSettingsBeforeColorChange = this.settingsData.personalize
		},
		restorePreviouslySelectedColors() {
			this.mergePersonalizationFormData(this.personalizeSettingsBeforeColorChange)
		},
		/**
		 * Checks if selected configs required fields are filled before saving
		 */
		checkIfConfigSpecificValuesExists() {
			let errorMessage: string | undefined
			/**
			 * Without itemType id form will be empty
			 */
			if (this.selectedFormsConfig?.itemType.id) {
				this.selectedFormsConfig?.collections.forEach((item) => {
					if (!item.projectId) return (errorMessage = "data-saving-errors.no-project")
					if (!item.folderId) return (errorMessage = "data-saving-errors.no-folder")
				})
			}

			return errorMessage
		},
		/**
		 * Stores selected config before edit
		 */
		selectedConfigFormBeforeEdit: selectedConfigData,
		resetSelectedConfigFormBeforeEdit() {
			this.selectedConfigFormBeforeEdit = null
		},
		setSelectedConfigFormBeforeEdit() {
			this.selectedConfigFormBeforeEdit = this.selectedFormsConfig
		},
		checkIfFormIsEdited() {
			// !TECHNICAL DEBT: REMOVE THIS AND ADD IF WHEN THIS IS CALLED
			// IF ITEM ID IS KNOWN THEN WAIT
			// WHILE DATA IS STILL LOADING
			// MEANWHILE RETURN TRUE
			if (this.selectedFormsConfig?.itemType?.id && this.mainFieldsData?.length == 0) return true
			return _.isEqual(
				JSON.parse(JSON.stringify(this.selectedConfigFormBeforeEdit)),
				JSON.parse(JSON.stringify(this.selectedFormsConfig))
			)
		},

		// USED WHEN CREATING CONFIG FROM OTHER CONFIGS DATA
		copiedConfig: selectedDefaultConfigData,
		setCopiedConfig(config: IFormsConfig | undefined) {
			return (this.copiedConfig = config)
		},

		restoreConfigFormToPreviousState() {
			if (this._selectedFormPosition == null) return null
			this.settingsData.formsConfigs[this._selectedFormPosition] = {
				...this.settingsData.formsConfigs[this._selectedFormPosition],
				...this.selectedConfigFormBeforeEdit,
			}
		},
		selectedFormName: "",
		setSelectedFormName(name: string) {
			this.selectedFormName = name
		},

		userFormItemOfTypeLevels: defaultUserFormItemOfTypeLevels,
		resetUserFormItemOfTyleLevels() {
			this.userFormItemOfTypeLevels = { ...defaultUserFormItemOfTypeLevels }
		},
		setUserFormItemOfTypeLevels(key: string, value: string | undefined | TReactSelectOption[]) {
			this.userFormItemOfTypeLevels = { ...this.userFormItemOfTypeLevels, [key]: value }
		},
	}
}

/**
 * Typed variables
 */ /** */
const settingsData: ISettings = defaultSettingsData
const mainFieldsData: IMainField[] | null = null
const projectsAndFoldersData: IJamaProjectsAndFolders[] | null = null
const userFormFields: INewRequestFormField[] = []
const selectedFormPosition: number | null = null
const selectedForm: string = ""
const selectedConfigData: IFormsConfig | null = defaultConfigData
const selectedDefaultConfigData: IFormsConfig | undefined = undefined
const defaultLanguage: TLanguageValues = "en"
/**
 * Errors
 */ /** */
export const SLCTD_FORM_POS_NULL_ERR = new Error(
	`A data modifying or merging function was called before setting selected form's position through "setSelectedFormsConfig". This should not be the case.`
)
